/**
 * ProductListContainer
 * --
 */
import { useState, useReducer, useEffect } from 'react';
import ProductListPresenter from './ProductListPresenter';
import MessageAlert from 'utils';
import {
  useDeleteProduct,
  useUpdateProduct,
  useUpsertProduct,
} from 'hooks/ProductHooks';
import { Content, Row, Col, ModalLayout } from 'components';
import { Button } from 'antd';
import { useQueryClient } from '@tanstack/react-query';

/**----------------------------------------
 * [Component] 총관리자: 상품목록 - 컨테이너
 * ----------------------------------------
 */
const ProductListContainer = ({ defaultShopId, permission }) => {
  /* ===== INITIAL ===== */
  const queryClient = useQueryClient();
  const {
    deleteProduct,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
  } = useDeleteProduct();

  // console.log('permission ====>', permission);

  /* ===== Mutate ===== */
  const { updateProduct } = useUpdateProduct();
  const { upsertProduct } = useUpsertProduct();

  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [currentShop, setCurrentShop] = useState(defaultShopId ?? null);
  // 삭제 컨펌 모달
  const [isOpenDeleteConfirm, setIsOpenDeleteConfirm] = useState(false);
  // 페이징
  const [currentPage, setCurrentPage] = useState(1);
  // 전체 선택
  const [isCheckedAll, setIsCheckedAll] = useReducer((state, action) => {
    switch (action.type) {
      case 'TOGGLE':
        return !state;
      case 'RESET':
        return false;
      default:
        return state;
    }
  }, false);
  // 선택 목록
  const [checkedList, setCheckedList] = useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**--------------------
   * 페이징 처리
   * --------------------
   */
  const handlePagination = (page) => {
    if (currentPage === page) return;

    setCurrentPage(page);
  };

  /**--------------------
   * 체크박스 핸들러
   * --------------------
   * @param {string} type
   * @param {object | array} obj
   */
  const handleChecked = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setCheckedList((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setCheckedList((prev) =>
          prev.filter((select) => select.key !== obj.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setCheckedList(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = checkedList.filter((user) =>
          obj.some((ob) => ob.key === user.key)
        );

        setCheckedList(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = checkedList.length === obj.length;

        if (!isAll) {
          setCheckedList(obj);
        } else {
          setCheckedList([]);
        }
        return;
      default:
        return;
    }
  };

  /**--------------------
   *
   * --------------------
   */
  const handleConfirmDeleteProduct = () => {
    if (checkedList.length === 0) {
      MessageAlert.warning('삭제하실 상품을 선택해주세요.');
      return;
    } else if (checkedList.length > 1) {
      MessageAlert.warning('1건만 삭제 가능합니다.');
      return;
    }

    setIsOpenDeleteConfirm(true);
  };

  /**--------------------
   *
   * --------------------
   */
  const handleDeleteProduct = async () => {
    if (checkedList.length === 1) {
      const response = await deleteProduct(checkedList[0].key);

      if (response && response.status === 200) {
        queryClient.resetQueries({ queryKey: ['product'] });
        MessageAlert.success('상품을 삭제하였습니다.');
      } else {
        MessageAlert.warning('상품 삭제에 실패하였습니다.');
      }

      setIsOpenDeleteConfirm(false);
    }
  };

  /**
   * 상품 진열 상태 변경
   * --
   * @param {number} idx
   * @param {string} key
   * @param {string} value
   * @param {array} arr
   * @param {func} callback
   */
  const handleToggleProduct = async (idx, key, value, arr = [], callback) => {
    const msg1 = key === 'is_best' ? '베스트 상품' : key === 'is_new' ? '신상품' : '브랜드';
    const msg2 = value ? '진열' : '진열 해제';

    if (permission !== 'ADMIN') {
      MessageAlert.info(`${msg1} ${msg2}은/는 총관리자만 가능합니다`);
      return
    }

    try {
      const { status } = await updateProduct({
        product_id: idx,
        [key]: value ? 'Y' : 'N',
      });

      if (status !== 200) {
        MessageAlert.success(`${msg1}에 ${msg2} 실패하였습니다.`);
        return;
      }

      const fid = arr.findIndex((item) => item.product_id === idx);

      if (fid !== -1) {
        callback((prev) => [
          ...prev.slice(0, fid),
          { ...prev[fid], [key]: value ? 'Y' : 'N' },
          ...prev.slice(fid + 1),
        ]);
      }

      MessageAlert.success(`${msg1}에 ${msg2}되었습니다.`);
    } catch (err) {
      MessageAlert.error('상품 진열에 실패하였습니다.');
      console.error('[handleToggleProduct] Error!! ', err);
    }
  };

  /**
   * 상품 Bulk 수정
   * --
   * @param {string} key
   * @param {string} value
   * @param {string} message
   */
  const handleBulkProduct = async (key, value, message = null) => {
    if (!checkedList.length) {
      MessageAlert.info('변경할 상품을 선택해주세요!');
      return;
    }

    const confirm = (msg = '') => window.confirm(msg);

    try {
      if (
        !confirm(
          `${checkedList.length}개의 상품 ${message ?? '상태'
          }를 변경하시겠습니까?`
        )
      )
        return;

      const updateData = checkedList.map((product) => ({
        product_id: product.key,
        [key]: value,
      }));

      const { status } = await upsertProduct(updateData);

      if (status !== 200) {
        MessageAlert.info(
          `${checkedList.length}개의 상품 ${message ?? '상태'
          } 수정에 실패하였습니다.`
        );
        return;
      }

      queryClient.resetQueries({ queryKey: ['product'] });
      MessageAlert.success(
        `${checkedList.length}개의 상품 ${message ?? '상태'}가 수정되었습니다.`
      );
      setCheckedList([]);
    } catch (err) {
      console.error(`[handleBulkProduct] Error!! ${err}`);
      MessageAlert.error('에러발생!');
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  /* ===== HOOKS ===== */
  /**--------------------
   * 페이지 변경 시 체크박스 초기화
   * --------------------
   */
  useEffect(() => {
    setIsCheckedAll({ type: 'RESET' });
    setCheckedList([]);
  }, [currentPage]);

  /**--------------------
   * 업체 변경 시 체크박스 초기화
   * --------------------
   */
  useEffect(() => {
    setIsCheckedAll({ type: 'RESET' });
    setCheckedList([]);
  }, [currentShop]);

  /**--------------------
   *
   * --------------------
   */
  useEffect(() => {
    if (isDeleteSuccess) {
      return;
    }

    if (isDeleteError) {
      MessageAlert.error('상품 삭제에 실패하였습니다.');
      return;
    }
  }, [isDeleteError, isDeleteSuccess]);

  /* ===== RENDER ===== */
  // JSX: start
  return (
    <>
      <ProductListPresenter
        // State
        loading={loading}
        defaultShopId={defaultShopId}
        permission={permission}
        currentPage={currentPage}
        isCheckedAll={isCheckedAll}
        checkedList={checkedList}
        currentShop={currentShop}
        // Function
        setCurrentShop={setCurrentShop}
        onPagination={handlePagination}
        onChecked={handleChecked}
        onCheckedAll={setIsCheckedAll}
        onDelete={handleConfirmDeleteProduct}
        onToggle={handleToggleProduct}
        setLoading={setLoading}
        onBulk={handleBulkProduct}
      />
      <ModalLayout
        type={'modal'}
        open={isOpenDeleteConfirm}
        onCancel={() => setIsOpenDeleteConfirm(false)}
        centered
        closable
        title={'상품을 삭제 하시겠습니까?'}
        width={300}
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Row>
              <Col x={24}>
                <Button onClick={() => setIsOpenDeleteConfirm(false)}>
                  취소
                </Button>
                &nbsp;
                <Button
                  type="primary"
                  onClick={() => {
                    handleDeleteProduct();
                  }}
                >
                  삭제
                </Button>
              </Col>
            </Row>
          </Content>
        }
      />
    </>
  ); // JSX: end
};

ProductListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default ProductListContainer;
